import { motion } from 'framer-motion';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { Link } from 'react-router-dom';

export const Cart = ({ cart, onUpdateQuantity, onRemoveItem }) => {
  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shipping = 100; // Always charge shipping, calculated at checkout
  const total = subtotal + shipping;

  if (cart.length === 0) {
    return (
      <div data-testid="cart-page" className="min-h-screen pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <ShoppingBag className="mx-auto text-muted-foreground mb-6" size={64} strokeWidth={1} />
          <h1 className="font-display text-4xl mb-4">Your Cart is Empty</h1>
          <p className="text-muted-foreground mb-8">Discover our exquisite collection of fragrances.</p>
          <Link
            to="/products"
            data-testid="empty-cart-shop-button"
            className="inline-block bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-10 py-4 uppercase tracking-widest text-sm font-medium transition-all duration-300 hover:scale-105"
          >
            Shop Now
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="min-h-screen pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="font-display text-5xl md:text-6xl font-normal tracking-tight text-foreground mb-12">
            Shopping <span className="italic">Cart</span>
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-6" data-testid="cart-items">
              {cart.map((item) => (
                <div
                  key={item.cartItemId}
                  data-testid={`cart-item-${item.id}`}
                  className="flex gap-6 p-6 border border-white/5 hover:border-white/10 transition-colors duration-300"
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-24 h-24 object-cover"
                  />
                  <div className="flex-1">
                    <h3 className="font-display text-xl mb-1">{item.name}</h3>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
                      {item.category}
                    </p>
                    <p className="font-accent text-xl italic text-primary">
                      ₹{item.price.toFixed(2)}
                    </p>
                  </div>
                  <div className="flex flex-col items-end justify-between">
                    <button
                      onClick={() => onRemoveItem(item.cartItemId)}
                      data-testid={`remove-item-${item.id}`}
                      className="text-muted-foreground hover:text-destructive transition-colors duration-300"
                    >
                      <Trash2 size={20} strokeWidth={1.5} />
                    </button>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => onUpdateQuantity(item.cartItemId, item.quantity - 1)}
                        data-testid={`decrease-quantity-${item.id}`}
                        className="w-8 h-8 border border-white/20 hover:border-primary transition-colors duration-300 flex items-center justify-center"
                      >
                        <Minus size={14} strokeWidth={1.5} />
                      </button>
                      <span className="w-8 text-center" data-testid={`quantity-${item.id}`}>{item.quantity}</span>
                      <button
                        onClick={() => onUpdateQuantity(item.cartItemId, item.quantity + 1)}
                        data-testid={`increase-quantity-${item.id}`}
                        className="w-8 h-8 border border-white/20 hover:border-primary transition-colors duration-300 flex items-center justify-center"
                      >
                        <Plus size={14} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-32 border border-white/10 p-8 space-y-6" data-testid="cart-summary">
                <h2 className="font-display text-2xl mb-6">Order Summary</h2>
                
                <div className="space-y-4 pb-6 border-b border-white/10">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span data-testid="cart-subtotal">₹{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Shipping</span>
                    <span data-testid="cart-shipping">Calculated at checkout</span>
                  </div>
                  <p className="text-xs text-muted-foreground italic">
                    Shipping charges calculated by BlueDart based on location
                  </p>
                </div>

                <div className="flex justify-between text-lg font-medium pt-2">
                  <span>Total</span>
                  <span className="font-accent text-2xl italic text-primary" data-testid="cart-total">
                    ₹{total.toFixed(2)}
                  </span>
                </div>

                <Link
                  to="/checkout"
                  data-testid="proceed-to-checkout-button"
                  className="block w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-4 uppercase tracking-widest text-sm font-medium transition-all duration-300 hover:scale-105 text-center"
                >
                  Proceed to Checkout
                </Link>

                <Link
                  to="/products"
                  data-testid="continue-shopping-button"
                  className="block w-full text-center text-primary hover:text-primary/80 uppercase tracking-widest text-sm font-medium hover:underline underline-offset-8"
                >
                  Continue Shopping
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};